import { useEffect, useState } from "react";
import { ArrowsClockwise, ChartLineUp } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";

/**
 * Task SLA / throughput report — Admin & Owner only.
 * Shows completed-task volume, average hours to complete, and open backlog by
 * task type and by assignee over a configurable window.
 */
export default function TaskSlaReport() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const r = await api.get(`/tasks-report/sla`, { params: { days: d } });
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days]);

  return (
    <div data-testid="task-sla-report">
      <PageHeader
        title="Task SLA Report"
        description={`Team throughput and average completion time over the last ${days} days`}
        rightSlot={
          <div className="flex items-center gap-2">
            <select
              data-testid="sla-window"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button
              onClick={() => load(days)}
              className="rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              <ArrowsClockwise size={14} weight="bold" />
            </button>
          </div>
        }
      />

      {loading || !data ? (
        <div className="rounded-md border border-gray-200 bg-white p-6 text-sm text-gray-500">
          Loading report…
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Kpi label="Completed" value={data.completed_total} />
            <Kpi label="Avg time to complete" value={`${data.avg_hours}h`} />
            <Kpi label="Window" value={`${data.window_days} days`} tone="slate" />
          </div>

          <Section title="By Task Type" data-testid="sla-by-type">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-[#f9fafb] text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-3 py-1.5 font-semibold">Task Type</th>
                  <th className="px-3 py-1.5 text-right font-semibold">Completed</th>
                  <th className="px-3 py-1.5 text-right font-semibold">Avg Hours</th>
                  <th className="px-3 py-1.5 text-right font-semibold">Open Backlog</th>
                </tr>
              </thead>
              <tbody>
                {data.by_type.length === 0 ? (
                  <tr><td colSpan={4} className="px-3 py-4 text-center text-gray-500">No completed tasks in this window</td></tr>
                ) : (
                  data.by_type.map((r) => (
                    <tr key={r.task_type} className="border-b border-gray-100 last:border-0">
                      <td className="px-3 py-2 text-gray-900">{r.task_type}</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-800">{r.completed}</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-800">{r.avg_hours}h</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-800">{r.open}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Section>

          <Section title="By Assignee" data-testid="sla-by-assignee">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-[#f9fafb] text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-3 py-1.5 font-semibold">Assignee</th>
                  <th className="px-3 py-1.5 text-right font-semibold">Completed</th>
                  <th className="px-3 py-1.5 text-right font-semibold">Avg Hours</th>
                </tr>
              </thead>
              <tbody>
                {data.by_assignee.length === 0 ? (
                  <tr><td colSpan={3} className="px-3 py-4 text-center text-gray-500">No completed tasks in this window</td></tr>
                ) : (
                  data.by_assignee.map((r) => (
                    <tr key={r.assignee} className="border-b border-gray-100 last:border-0">
                      <td className="px-3 py-2 text-gray-900">{r.assignee}</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-800">{r.completed}</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-800">{r.avg_hours}h</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Section>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, tone }) {
  return (
    <div className="rounded-md border border-gray-200 bg-white p-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`mt-1 text-3xl font-bold ${tone === "slate" ? "text-gray-500" : "text-gray-900"}`}>{value}</div>
    </div>
  );
}

function Section({ title, children, ...rest }) {
  return (
    <section {...rest}>
      <div className="mb-2 flex items-center gap-2">
        <ChartLineUp size={14} weight="bold" className="text-gray-500" />
        <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-700">{title}</h3>
      </div>
      <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
        {children}
      </div>
    </section>
  );
}
